import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AxiosClient from '@/api/client';
import type {
  AssessmentPerformanceData,
  AssessmentPerformanceTouchpoint,
  UpdateAnswerRequest,
  ApiAssessmentMedia,
} from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const assessmentPerformanceKeys = {
  all: ['assessment-performance'] as const,
  detail: (id: string) => [...assessmentPerformanceKeys.all, id] as const,
  navigation: (id: string) =>
    [...assessmentPerformanceKeys.all, id, 'navigation'] as const,
  touchpoint: (id: string, touchpointId: number) =>
    [...assessmentPerformanceKeys.all, id, 'touchpoint', touchpointId] as const,
  media: (id: string) =>
    [...assessmentPerformanceKeys.all, id, 'media'] as const,
};

export function useAssessmentNavigationQuery(assessmentId: string) {
  return useQuery({
    queryKey: assessmentPerformanceKeys.navigation(assessmentId),
    queryFn: async () => {
      const client = new AxiosClient<
        void,
        {
          assessment: AssessmentPerformanceData['assessment'];
          scope: AssessmentPerformanceData['scope'];
          statistics: {
            total_questions: number;
            answered_questions: number;
            completion_percentage: number;
          };
        }
      >({
        endpoint: `/assessments/${assessmentId}/navigation`,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.detail || 'Failed to fetch navigation data');
      }

      return response.data;
    },
    enabled: !!assessmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

// ============================================================================
// Fetch Touchpoint Standards
// ============================================================================

/**
 * Hook to fetch standards for a specific department + touchpoint combination
 * GET /api/assessments/{id}/department/{departmentId}/touchpoint/{touchpointId}
 */
export function useTouchpointStandardsQuery(
  assessmentId: string,
  departmentId: number | null,
  touchpointId: number | null
) {
  return useQuery({
    queryKey: [
      ...assessmentPerformanceKeys.touchpoint(assessmentId, touchpointId || 0),
      departmentId,
    ],
    queryFn: async () => {
      if (!departmentId || !touchpointId) {
        throw new Error('Department ID and Touchpoint ID are required');
      }

      const client = new AxiosClient<void, AssessmentPerformanceTouchpoint>({
        endpoint: `/assessments/${assessmentId}/department/${departmentId}/touchpoint/${touchpointId}`,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(
          response.detail || 'Failed to fetch touchpoint standards'
        );
      }

      return response.data;
    },
    enabled: !!assessmentId && !!departmentId && !!touchpointId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ============================================================================
// Fetch Assessment Performance Data (Legacy - Full Data)
// ============================================================================

/**
 * Hook to fetch assessment data for performance (inspector interface)
 * GET /api/assessments/{id}/perform
 */
export function useAssessmentPerformQuery(assessmentId: string) {
  return useQuery({
    queryKey: assessmentPerformanceKeys.detail(assessmentId),
    queryFn: async () => {
      const client = new AxiosClient<void, AssessmentPerformanceData>({
        endpoint: `/assessments/${assessmentId}/perform`,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.detail || 'Failed to fetch assessment');
      }

      return response.data;
    },
    enabled: !!assessmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

// ============================================================================
// Update Answer
// ============================================================================

/**
 * Hook to update a single answer
 * PATCH /api/assessments/{assessmentId}/answers/{answerId}
 */
export function useUpdateAnswerMutation(assessmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      answerId,
      data,
    }: {
      answerId: string;
      data: UpdateAnswerRequest;
    }) => {
      const client = new AxiosClient<
        UpdateAnswerRequest,
        AssessmentPerformanceData
      >({
        endpoint: `/assessments/${assessmentId}/answers/${answerId}`,
      });

      const response = await client.patch(data);

      if (!response.status) {
        throw new Error(response.detail || 'Failed to update answer');
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.navigation(assessmentId),
      });
      // Also invalidate the specific touchpoint to refetch standards with updated answers
      queryClient.invalidateQueries({
        queryKey: ['assessment-performance', 'touchpoint', assessmentId],
      });
      // Invalidate assessments list to update completion percentage
      queryClient.invalidateQueries({
        queryKey: ['assessments'],
      });
    },
  });
}

// ============================================================================
// Submit Assessment
// ============================================================================

/**
 * Hook to submit assessment for review
 * PUT /api/assessments/{id}/submit
 */
export function useSubmitAssessmentMutation(assessmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assessmentData?: {
      items: Array<{
        answer_id?: string;
        standard_id: number;
        answer: string;
        comment?: string;
      }>;
    }) => {
      const client = new AxiosClient<
        typeof assessmentData,
        { message: string }
      >({
        endpoint: `/assessments/${assessmentId}/submit`,
      });

      // Send assessment data if provided
      const response = assessmentData
        ? await client.put(assessmentData)
        : await client.put();

      if (!response.status) {
        throw new Error(response.detail || 'Failed to submit assessment');
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.detail(assessmentId),
      });
      queryClient.invalidateQueries({
        queryKey: ['assessments'],
      });
    },
  });
}

// ============================================================================
// Upload Media
// ============================================================================

/**
 * Hook to upload media (photo/audio) for assessment touchpoint
 * POST /api/assessments/{assessmentId}/media
 */
export function useUploadMediaMutation(assessmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      media_type,
      assessment_answer_id,
      standard_id,
      caption,
    }: {
      file: File;
      media_type: 'photo' | 'audio';
      assessment_answer_id?: string;
      standard_id?: number;
      caption?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', media_type);

      if (assessment_answer_id) {
        formData.append('assessment_answer_id', assessment_answer_id);
      }
      if (standard_id) {
        formData.append('standard_id', standard_id.toString());
      }
      if (caption) {
        formData.append('caption', caption);
      }

      const client = new AxiosClient<FormData, ApiAssessmentMedia>({
        endpoint: `/assessments/${assessmentId}/media`,
        contentType: 'multipart/form-data',
      });

      const response = await client.post(formData);

      if (!response.status) {
        throw new Error(response.detail || 'Failed to upload media');
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate queries to refetch media list and counts
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.media(assessmentId),
      });
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.detail(assessmentId),
      });
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.navigation(assessmentId),
      });
    },
  });
}

// ============================================================================
// Get Assessment Media
// ============================================================================

/**
 * Hook to fetch all media for an assessment
 * GET /api/assessments/{id}/media
 */
export function useAssessmentMediaQuery(assessmentId: string) {
  return useQuery({
    queryKey: assessmentPerformanceKeys.media(assessmentId),
    queryFn: async () => {
      const client = new AxiosClient<void, ApiAssessmentMedia[]>({
        endpoint: `/assessments/${assessmentId}/media`,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error('Failed to fetch media');
      }

      return response.data;
    },
    enabled: !!assessmentId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Delete Media
// ============================================================================

/**
 * Hook to delete media
 * DELETE /api/assessments/{assessmentId}/media/{mediaId}
 */
export function useDeleteMediaMutation(assessmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const client = new AxiosClient<void, { message: string }>({
        endpoint: `/assessments/${assessmentId}/media/${mediaId}`,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.detail || 'Failed to delete media');
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate all media queries for this assessment
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.media(assessmentId),
      });
      // Invalidate navigation to update counts
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.navigation(assessmentId),
      });
      // Invalidate all touchpoint queries to update media counts
      queryClient.invalidateQueries({
        queryKey: assessmentPerformanceKeys.all,
      });
    },
  });
}
